// Autosave-to-Salesforce — pure logic behind CalculatorApp's throttled background sync.
//
// A leaf module: importable from a test without the React/asset import graph. No React imports,
// no asset imports. (v3's dirty gate is state/autosave.ts; this file keeps the throttle and the
// status-chip copy it shares.)
//
// The throttle model (user-confirmed design):
//   - A story-affecting edit marks the state dirty; the FIRST push after a clean state goes
//     out after a short settle (~30s) so SFDC reflects new work quickly.
//   - After any push attempt, further pushes are gated to >= 15 minutes apart ("max one push
//     per 15m", not "wait for 15m of quiet") — SFDC never lags local state by more than ~15m
//     even during continuous editing. Server-side, same-day pushes collapse onto one
//     Value_Snapshot__c row (day-scoped External_ID__c upsert), so extra pushes are cheap.
//   - Failures keep the state dirty and retry on the same 15m cadence; force-sync pushes
//     immediately and resets the window.

export const AUTOSAVE_SETTLE_MS = 30_000;
export const AUTOSAVE_MIN_INTERVAL_MS = 15 * 60_000;

/**
 * The sync state machine's observable states (what the header chip renders):
 *   local-only — no linked account (unlinked or demo mode); autosave is inert.
 *   dirty      — unsynced local changes; a push is scheduled.
 *   syncing    — a push is in flight.
 *   synced     — local state matches the last successful push.
 *   error      — last push failed (generic); still dirty, retrying on the 15m cadence.
 *   reauth     — last push failed with SF_REAUTH_REQUIRED; the user must reconnect Salesforce.
 */
export type SyncStatus = 'local-only' | 'dirty' | 'syncing' | 'synced' | 'error' | 'reauth';

/**
 * When the next push may fire, given the throttle: never sooner than the settle delay after
 * becoming dirty, and never sooner than 15 minutes after the previous attempt. With no prior
 * attempt (lastAttemptAt = 0) or an old one, this returns the 30s settle; inside the 15m
 * window it returns the remainder.
 */
export function nextPushDelayMs(now: number, lastAttemptAt: number): number {
  return Math.max(AUTOSAVE_SETTLE_MS, lastAttemptAt + AUTOSAVE_MIN_INTERVAL_MS - now);
}

/**
 * Classify a failed push. `apiFetch` throws a MiniAppFetchError whose `code`/`oauthUrl` are
 * lifted from the problem body (`_shared/fetch-error.ts`); the server passes Middleman's
 * SF_REAUTH_REQUIRED through verbatim (`salesforceFailureProblem` → `problem()` extras). Duck-typed
 * rather than instanceof so a plain-object error (or a keepalive-path failure) classifies the
 * same way.
 */
export function classifySaveFailure(
  err: unknown,
): { kind: 'reauth'; oauthUrl: string | null } | { kind: 'error'; message: string } {
  const e = err as { code?: unknown; oauthUrl?: unknown; message?: unknown } | null;
  if (e && typeof e === 'object' && e.code === 'SF_REAUTH_REQUIRED') {
    return { kind: 'reauth', oauthUrl: typeof e.oauthUrl === 'string' ? e.oauthUrl : null };
  }
  const message =
    e && typeof e === 'object' && typeof e.message === 'string' && e.message
      ? e.message
      : 'sync failed';
  return { kind: 'error', message };
}

/**
 * Headers for the pagehide snapshot flush. That POST uses raw `fetch` (it needs `keepalive`,
 * which `apiFetch` does not pass), so it skips the `x-impersonate-write` header `apiFetch`
 * adds (`web/app-home/src/lib/api.ts`) and an unlocked impersonation session would 403 it.
 * `impersonateHeaders` is `impersonateWriteHeaders()` from the write-mode store.
 */
export function keepaliveFlushHeaders(impersonateHeaders: Record<string, string>): Record<string, string> {
  return { 'Content-Type': 'application/json', ...impersonateHeaders };
}

/**
 * Whether a snapshot flush must be skipped. Unlike a rep-unprompted ping, these are edits the
 * operator made on purpose, so an UNLOCKED impersonation still sends them (with
 * `keepaliveFlushHeaders`); only a locked one skips, since the server would refuse it.
 */
export function flushBlockedByImpersonation(s: { impersonating: boolean; writesUnlocked: boolean }): boolean {
  return s.impersonating && !s.writesUnlocked;
}

/** Chip tones map onto the app's existing status-pill palette (lime/lemon/pom/oat tokens). */
export type ChipTone = 'success' | 'warning' | 'error' | 'muted';

export interface SyncStatusView {
  label: string;
  chipTone: ChipTone;
  /**
   * True whenever there is something a push could fix — the chip becomes a force-sync button
   * (SyncStatus renders no separate button). Every such label ends in a verb phrase, since the
   * copy is the only cue that the chip is clickable.
   */
  forceSyncEnabled: boolean;
  /** True when the chip should reconnect Salesforce instead of force-syncing. */
  reconnect: boolean;
}

/** The header chip's copy + tone + affordances for each machine state. */
export function describeSyncStatus(
  status: SyncStatus,
  lastSyncedAt: number | null,
  now: number,
): SyncStatusView {
  switch (status) {
    case 'local-only':
      return {
        label: 'Local only — link an account to sync',
        chipTone: 'muted',
        forceSyncEnabled: false,
        reconnect: false,
      };
    case 'dirty':
      return {
        label: 'Unsaved — sync now',
        chipTone: 'warning',
        forceSyncEnabled: true,
        reconnect: false,
      };
    case 'syncing':
      return { label: 'Syncing…', chipTone: 'warning', forceSyncEnabled: false, reconnect: false };
    case 'synced':
      return {
        label: lastSyncedAt == null ? 'Synced' : `Synced ${formatRelativeTime(lastSyncedAt, now)}`,
        chipTone: 'success',
        forceSyncEnabled: false,
        reconnect: false,
      };
    case 'error':
      return { label: 'Sync failed — retry', chipTone: 'error', forceSyncEnabled: true, reconnect: false };
    case 'reauth':
      return {
        label: 'Salesforce disconnected — reconnect',
        chipTone: 'error',
        forceSyncEnabled: true,
        reconnect: true,
      };
  }
}

/**
 * Post-flush baseline resolution. A flush (unmount / account switch / pagehide) advances
 * the baseline to the flushed fingerprint only once the POST is KNOWN to have landed —
 * and only if the baseline is still the one captured at flush time. A baseline
 * re-anchored mid-flight (successful push, account switch, demo-enter) must win over a
 * late flush success: the re-anchor reflects newer truth. A failed flush never advances,
 * so the state stays dirty and a bfcache return re-flushes it (duplicate POSTs are
 * idempotent via the day-scoped External_ID__c upsert).
 */
export function nextBaselineAfterFlush(
  current: string | null,
  baselineAtFlush: string | null,
  flushedFp: string,
): string | null {
  return current === baselineAtFlush ? flushedFp : current;
}

/**
 * Hand-rolled relative time (no date-fns in this repo); same shape as the launcher app's
 * convention: "just now" / "Xm ago" / "Xh ago" / "Xd ago".
 */
export function formatRelativeTime(thenMs: number, nowMs: number): string {
  const diffMin = Math.floor((nowMs - thenMs) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}
